/**
 * The closed operation registry.
 *
 * This is the single hard boundary between the language model and the pixels.
 * The planner may emit operations from this list and nothing else; anything
 * unrecognised, out of range or wrongly typed is rejected by `validatePlan`
 * before it can reach the renderer (PLANT.md `AI-01`, `AI-08`, §9.8).
 *
 * Adding an entry here is a deliberate product decision: every operation must
 * be inspectable in the UI, reversible, and expressible as an ordinary
 * photographic control.
 */

import type { Recipe } from './recipe.ts';

/** UX grouping used by both the beginner plan view and the expert panels. */
export type OperationGroup = 'light' | 'color' | 'subject' | 'composition' | 'look';

export interface ScalarOperationSpec {
  id: string;
  kind: 'scalar';
  /** Path into the recipe. Dotted for nested fields, e.g. `hsl.orange.saturation`. */
  path: string;
  group: OperationGroup;
  label: string;
  unit: string;
  min: number;
  max: number;
  /** Value that means "no change". */
  neutral: number;
  /** Sensible per-step for the UI and for bounding an AI move. */
  step: number;
  /** What the control does, in the words a photographer would use. */
  description: string;
  /**
   * Largest single change the planner may make in one proposal without the
   * user explicitly asking for something drastic. Guards `R-03`.
   */
  maxDelta?: number;
}

export interface CurveOperationSpec {
  id: string;
  kind: 'curve';
  path: string;
  group: OperationGroup;
  label: string;
  description: string;
}

export interface MaskOperationSpec {
  id: string;
  kind: 'mask';
  group: OperationGroup;
  label: string;
  description: string;
}

export type OperationSpec = ScalarOperationSpec | CurveOperationSpec | MaskOperationSpec;

const scalar = (
  id: string,
  path: string,
  group: OperationGroup,
  label: string,
  unit: string,
  min: number,
  max: number,
  neutral: number,
  step: number,
  description: string,
  maxDelta?: number,
): ScalarOperationSpec => ({
  id,
  kind: 'scalar',
  path,
  group,
  label,
  unit,
  min,
  max,
  neutral,
  step,
  description,
  maxDelta,
});

const HSL_BAND_IDS = [
  'red',
  'orange',
  'yellow',
  'green',
  'aqua',
  'blue',
  'purple',
  'magenta',
] as const;

const GRADING_ZONE_IDS = ['shadows', 'midtones', 'highlights', 'global'] as const;

const hslOps: ScalarOperationSpec[] = HSL_BAND_IDS.flatMap((band) => [
  scalar(
    `hsl.${band}.hue`,
    `hsl.${band}.hue`,
    'color',
    `${band} hue`,
    '',
    -100,
    100,
    0,
    1,
    `Rotates hues inside the ${band} range without touching other colours.`,
    40,
  ),
  scalar(
    `hsl.${band}.saturation`,
    `hsl.${band}.saturation`,
    'color',
    `${band} saturation`,
    '',
    -100,
    100,
    0,
    1,
    `Strengthens or mutes only the ${band} range.`,
    50,
  ),
  scalar(
    `hsl.${band}.luminance`,
    `hsl.${band}.luminance`,
    'color',
    `${band} luminance`,
    '',
    -100,
    100,
    0,
    1,
    `Brightens or darkens only the ${band} range.`,
    50,
  ),
]);

const gradingOps: ScalarOperationSpec[] = GRADING_ZONE_IDS.flatMap((zone) => [
  scalar(
    `grading.${zone}.hue`,
    `grading.${zone}.hue`,
    'look',
    `${zone} grade hue`,
    '°',
    0,
    360,
    0,
    1,
    `Hue of the tint applied to the ${zone}.`,
  ),
  scalar(
    `grading.${zone}.saturation`,
    `grading.${zone}.saturation`,
    'look',
    `${zone} grade strength`,
    '',
    0,
    100,
    0,
    1,
    `How strongly the ${zone} tint is applied.`,
    40,
  ),
  scalar(
    `grading.${zone}.luminance`,
    `grading.${zone}.luminance`,
    'look',
    `${zone} grade luminance`,
    '',
    -100,
    100,
    0,
    1,
    `Lifts or drops the ${zone} independently of the tint.`,
    40,
  ),
]);

export const OPERATIONS: readonly OperationSpec[] = [
  // ---- Light -------------------------------------------------------------
  scalar(
    'exposure',
    'exposure',
    'light',
    'Exposure',
    'EV',
    -5,
    5,
    0,
    0.05,
    'Overall brightness in stops, applied to scene-linear values before any tone shaping.',
    2,
  ),
  scalar(
    'contrast',
    'contrast',
    'light',
    'Contrast',
    '',
    -100,
    100,
    0,
    1,
    'Steepens or flattens the mid-tones around a fixed pivot.',
    60,
  ),
  scalar(
    'highlights',
    'highlights',
    'light',
    'Highlights',
    '',
    -100,
    100,
    0,
    1,
    'Recovers or opens the brightest zones without moving the white point.',
    80,
  ),
  scalar(
    'shadows',
    'shadows',
    'light',
    'Shadows',
    '',
    -100,
    100,
    0,
    1,
    'Opens or deepens the darkest zones without moving the black point.',
    80,
  ),
  scalar(
    'whites',
    'whites',
    'light',
    'Whites',
    '',
    -100,
    100,
    0,
    1,
    'Sets where the image reaches white. Raising it can clip.',
    60,
  ),
  scalar(
    'blacks',
    'blacks',
    'light',
    'Blacks',
    '',
    -100,
    100,
    0,
    1,
    'Sets where the image reaches black. Lowering it can crush shadow detail.',
    60,
  ),

  // ---- Colour ------------------------------------------------------------
  scalar(
    'temperature',
    'temperature',
    'color',
    'Temperature',
    'K',
    2000,
    20000,
    5500,
    10,
    'White balance along the blue / yellow axis, in Kelvin. Lower is cooler.',
    2500,
  ),
  scalar(
    'tint',
    'tint',
    'color',
    'Tint',
    '',
    -150,
    150,
    0,
    1,
    'White balance along the green / magenta axis.',
    40,
  ),
  scalar(
    'vibrance',
    'vibrance',
    'color',
    'Vibrance',
    '',
    -100,
    100,
    0,
    1,
    'Raises saturation of muted colours first and protects skin tones.',
    60,
  ),
  scalar(
    'saturation',
    'saturation',
    'color',
    'Saturation',
    '',
    -100,
    100,
    0,
    1,
    'Raises saturation of every colour equally. Blunter than vibrance.',
    50,
  ),
  ...hslOps,

  // ---- Look --------------------------------------------------------------
  scalar(
    'texture',
    'texture',
    'look',
    'Texture',
    '',
    -100,
    100,
    0,
    1,
    'Fine local contrast: skin pores, fabric weave, foliage detail.',
    60,
  ),
  scalar(
    'clarity',
    'clarity',
    'look',
    'Clarity',
    '',
    -100,
    100,
    0,
    1,
    'Mid-radius local contrast. Adds punch, but hardens skin at high values.',
    50,
  ),
  scalar(
    'dehaze',
    'dehaze',
    'look',
    'Dehaze',
    '',
    -100,
    100,
    0,
    1,
    'Removes or adds atmospheric veiling by re-expanding the low-contrast haze layer.',
    50,
  ),
  ...gradingOps,
  scalar(
    'grading.blending',
    'grading.blending',
    'look',
    'Grade blending',
    '',
    0,
    100,
    50,
    1,
    'How much the shadow, mid-tone and highlight grades overlap.',
  ),
  scalar(
    'grading.balance',
    'grading.balance',
    'look',
    'Grade balance',
    '',
    -100,
    100,
    0,
    1,
    'Moves the split point between the shadow and highlight grades.',
  ),
  scalar(
    'vignetteAmount',
    'vignetteAmount',
    'look',
    'Vignette',
    '',
    -100,
    100,
    0,
    1,
    'Darkens or brightens the frame edges to steer attention.',
    50,
  ),
  scalar(
    'vignetteMidpoint',
    'vignetteMidpoint',
    'look',
    'Vignette midpoint',
    '',
    0,
    100,
    50,
    1,
    'How far into the frame the vignette reaches.',
  ),
  scalar(
    'vignetteFeather',
    'vignetteFeather',
    'look',
    'Vignette feather',
    '',
    0,
    100,
    50,
    1,
    'Softness of the vignette edge.',
  ),
  scalar(
    'vignetteRoundness',
    'vignetteRoundness',
    'look',
    'Vignette roundness',
    '',
    -100,
    100,
    0,
    1,
    'Shape of the vignette between rectangular and circular.',
  ),
  scalar(
    'grainAmount',
    'grainAmount',
    'look',
    'Grain',
    '',
    0,
    100,
    0,
    1,
    'Adds film-like luminance noise.',
    40,
  ),
  scalar('grainSize', 'grainSize', 'look', 'Grain size', '', 0, 100, 25, 1, 'Size of the grain structure.'),

  // ---- Detail ------------------------------------------------------------
  scalar(
    'sharpenAmount',
    'sharpenAmount',
    'look',
    'Sharpening',
    '',
    0,
    150,
    25,
    1,
    'Edge acutance via unsharp masking.',
    60,
  ),
  scalar('sharpenRadius', 'sharpenRadius', 'look', 'Sharpen radius', 'px', 0.5, 3, 1, 0.1, 'Size of the edges that sharpening acts on.'),
  scalar('sharpenDetail', 'sharpenDetail', 'look', 'Sharpen detail', '', 0, 100, 25, 1, 'How much fine texture the sharpening includes.'),
  scalar(
    'sharpenMasking',
    'sharpenMasking',
    'look',
    'Sharpen masking',
    '',
    0,
    100,
    0,
    1,
    'Restricts sharpening to edges, protecting smooth skin and sky.',
  ),
  scalar(
    'noiseLuminance',
    'noiseLuminance',
    'look',
    'Luminance noise reduction',
    '',
    0,
    100,
    0,
    1,
    'Smooths luminance grain. High values soften real texture.',
    60,
  ),
  scalar('noiseLuminanceDetail', 'noiseLuminanceDetail', 'look', 'Noise detail', '', 0, 100, 50, 1, 'How much detail luminance noise reduction preserves.'),
  scalar(
    'noiseColor',
    'noiseColor',
    'look',
    'Colour noise reduction',
    '',
    0,
    100,
    25,
    1,
    'Removes chroma blotches from high-ISO captures.',
    60,
  ),

  // ---- Composition -------------------------------------------------------
  scalar('rotate', 'rotate', 'composition', 'Straighten', '°', -45, 45, 0, 0.1, 'Rotates the frame to level a horizon or vertical.', 10),

  // ---- Curves ------------------------------------------------------------
  {
    id: 'curve.rgb',
    kind: 'curve',
    path: 'curves.rgb',
    group: 'light',
    label: 'Tone curve',
    description:
      'The master tone curve. Shapes contrast, the shoulder and the toe with more control than the tone sliders.',
  },
  {
    id: 'curve.red',
    kind: 'curve',
    path: 'curves.red',
    group: 'color',
    label: 'Red curve',
    description: 'Per-channel red curve. Warms or cools specific tonal ranges.',
  },
  {
    id: 'curve.green',
    kind: 'curve',
    path: 'curves.green',
    group: 'color',
    label: 'Green curve',
    description: 'Per-channel green curve. Moves the green / magenta balance by tonal range.',
  },
  {
    id: 'curve.blue',
    kind: 'curve',
    path: 'curves.blue',
    group: 'color',
    label: 'Blue curve',
    description:
      'Per-channel blue curve. Lifting the toe and dropping the shoulder gives the classic split-toned look.',
  },

  // ---- Local -------------------------------------------------------------
  {
    id: 'mask.add',
    kind: 'mask',
    group: 'subject',
    label: 'Local adjustment',
    description:
      'Adds an editable radial, linear, luminance-range or colour-range mask carrying its own local adjustments.',
  },
] as const;

export const OPERATIONS_BY_ID: ReadonlyMap<string, OperationSpec> = new Map(
  OPERATIONS.map((op) => [op.id, op]),
);

export const SCALAR_OPERATIONS: readonly ScalarOperationSpec[] = OPERATIONS.filter(
  (op): op is ScalarOperationSpec => op.kind === 'scalar',
);

/** Reads a dotted path out of a recipe. */
export function readPath(recipe: Recipe, path: string): unknown {
  return path.split('.').reduce<unknown>((node, key) => {
    if (node === null || typeof node !== 'object') return undefined;
    return (node as Record<string, unknown>)[key];
  }, recipe);
}

/** Writes a dotted path into a recipe, in place. */
export function writePath(recipe: Recipe, path: string, value: unknown): void {
  const keys = path.split('.');
  const last = keys.pop();
  if (!last) return;
  let node: Record<string, unknown> = recipe as unknown as Record<string, unknown>;
  for (const key of keys) {
    const next = node[key];
    if (next === null || typeof next !== 'object') return;
    node = next as Record<string, unknown>;
  }
  node[last] = value;
}

export function clampToSpec(spec: ScalarOperationSpec, value: number): number {
  if (!Number.isFinite(value)) return spec.neutral;
  return Math.min(spec.max, Math.max(spec.min, value));
}
